import {LayerLegend as Legend} from '@src/types/configurations';

export function getMinMaxPosition(legend: Legend) {
  let minPosition = legend.minPosition ?? 0.0;
  let maxPosition = legend.maxPosition ?? 1.0;

  legend.gradients.forEach(el => {
    minPosition = Math.min(minPosition, el.position);
    maxPosition = Math.max(maxPosition, el.position);
  });

  return {minPosition, maxPosition};
}

export function createGradientStops(
  legend: Legend,
  minPosition: number,
  maxPosition: number,
): string {
  const rules = legend.gradients
    .filter(g => g.color)
    .map(r => ({
      ...r,
      relPosition: (r.position - minPosition) / (maxPosition - minPosition),
    }))
    .sort((a, b) => a.relPosition - b.relPosition);

  if (rules.length === 0) {
    return '';
  }

  const stops: string[] = [];

  // Fill the remaining space at start
  if (rules[0].relPosition > 0.0) {
    stops.push(`${rules[0].color} 0%`);
    stops.push(`${rules[0].color} ${(rules[0].relPosition * 100).toFixed(2)}%`);
  } else {
    stops.push(`${rules[0].color} ${(rules[0].relPosition * 100).toFixed(2)}%`);
  }

  // Add all intermediate gradient stops
  for (let i = 1; i < rules.length; i++) {
    const currentPos = (rules[i].relPosition * 100).toFixed(2);
    const prevPos = (rules[i - 1].relPosition * 100).toFixed(2);

    // Skip if at exact same position
    if (currentPos !== prevPos) {
      stops.push(`${rules[i].color} ${currentPos}%`);
    }
  }

  // Fill the remaining space at end
  const lastRule = rules[rules.length - 1];
  if (lastRule.relPosition < 1.0) {
    stops.push(`${lastRule.color} 100%`);
  }

  return stops.join(', ');
}

export function groupLabeledStops(legend: Legend) {
  const labeledGradients = legend.gradients.filter(g => !!g.label);
  const map = new Map<number, string[]>();

  labeledGradients.forEach(g => {
    const pos = g.position;
    const existing = map.get(pos) ?? [];
    const label = String(g.label).trim();
    if (label && !existing.includes(label)) {
      existing.push(label);
    }
    map.set(pos, existing);
  });

  return Array.from(map.entries())
    .map(([position, labels]) => ({position, labels}))
    .sort((a, b) => a.position - b.position);
}
